// ══════════════════════════════════════════════════════════════════════════════
//  src/components/teacher/ModalEvaluar.jsx
//  LO QUE EL PROFESOR OBSERVA DE UN ALUMNO
//
//  ── LA DECISION QUE ORGANIZA ESTA PANTALLA ──────────────────────────────────
//  Rob:
//    «Si el alumno ve que un profesor humano le puso un 3/5, rompemos la
//     promesa de ser un gimnasio seguro y generamos ansiedad. Como dato interno
//     es oro puro: sirve para que el algoritmo ajuste la dificultad de La Arena
//     o recomiende pildoras del Consultorio.»
//
//  Eso no es solo una regla del servidor: **el profesor tiene que saberlo
//  mientras escribe**. Si cree que el alumno va a leer esto, se autocensura y
//  pone cuatros a todo el mundo — y entonces el dato deja de servirle al motor,
//  que era el unico motivo de pedirlo. Por eso el aviso de que es interna esta
//  arriba del todo y no escondido en un pie de pagina.
//
//  ── POR QUE 1-5 CON BOTONES Y NO UN DESPLEGABLE ─────────────────────────────
//  Esto se rellena al acabar una clase, muchas veces desde el movil y con prisa.
//  Cinco botones son tres toques; un desplegable son cinco y una lista que tapa
//  la pantalla.
// ══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { LIMITES, avisaDelTope } from '../../limites.js';
import { useEvaluar } from './useProfesor.js';

const SENALES = [
  {
    clave: 'comprension',
    titulo: 'Comprension',
    pie: ['No sigue', 'Sigue todo'],
  },
  {
    clave: 'fluidez',
    titulo: 'Fluidez',
    pie: ['Muy trabada', 'Corre sola'],
  },
  {
    clave: 'confianza',
    titulo: 'Confianza',
    pie: ['Se bloquea', 'Se lanza'],
  },
];

const DIFICULTADES = [
  { valor: 'mas_facil',   etiqueta: 'Bajar' },
  { valor: 'mantener',    etiqueta: 'Mantener' },
  { valor: 'mas_dificil', etiqueta: 'Subir' },
];

export default function ModalEvaluar({ alumno, clase, onCerrar }) {
  const [comprension, setComprension] = useState(3);
  const [fluidez, setFluidez]         = useState(3);
  const [confianza, setConfianza]     = useState(3);
  const [dificultad, setDificultad]   = useState('mantener');
  const [nota, setNota]               = useState('');

  const evaluar = useEvaluar();

  const valores = { comprension, fluidez, confianza };
  const ponValor = {
    comprension: setComprension,
    fluidez: setFluidez,
    confianza: setConfianza,
  };

  const tope  = LIMITES.notaEvaluacion;
  const cerca = avisaDelTope(nota.length, tope);
  const lleno = nota.length >= tope;

  /**
   * El recorte va aqui, en el `setState`, y no solo en el `maxLength`.
   *
   * `maxLength` frena a quien teclea o pega, pero se quita desde las
   * herramientas del navegador en dos clics y no actua sobre nada que entre
   * por programa. El setter es el unico punto por el que pasan todos los
   * caminos, incluidos los que se escriban despues.
   */
  const escribeNota = (texto) => setNota(String(texto).slice(0, tope));

  const guardar = () => {
    if (evaluar.isPending) return;
    evaluar.mutate({
      alumnoId: alumno.id,
      comprension,
      fluidez,
      confianza,
      dificultad,
      ...(nota.trim() ? { nota: nota.trim() } : {}),
      // Si se entra desde una clase concreta, la evaluacion queda anclada a
      // ella. Si se entra desde la ficha del alumno, es una evaluacion suelta
      // y estos dos campos no se mandan: el servidor los acepta vacios.
      ...(clase?.fecha ? { fecha_clase: clase.fecha } : {}),
      ...(clase?.hora  ? { hora_clase: String(clase.hora) } : {}),
    });
  };

  // Cuando ya se guardo, la pantalla no vuelve al formulario: confirma y cierra.
  if (evaluar.isSuccess) {
    return (
      <div className="pp-overlay" onClick={onCerrar}>
        <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
          <div className="pp-modal-t">Guardado</div>
          <p className="pp-modal-s">{evaluar.data?.message}</p>
          <div className="pp-privado">
            <span aria-hidden="true">🔒</span>
            <span>
              Promedio <b>{evaluar.data?.promedio}</b>. Queda en tu historial de
              este alumno y alimenta la dificultad de sus ejercicios.
              <b> El alumno no lo ve.</b>
            </span>
          </div>
          <div className="pp-acciones">
            <button className="pp-btn pp-btn-ac" onClick={onCerrar}>Cerrar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-overlay" onClick={onCerrar}>
      <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-t">Evaluar a {alumno.nombre}</div>
        <p className="pp-modal-s">
          {clase?.fecha
            ? `Clase del ${clase.fecha}${clase.hora ? `, ${clase.hora}:00` : ''}.`
            : 'Evaluacion general, sin clase concreta.'}
        </p>

        {/* Arriba del todo y no en un pie de pagina: si el profesor cree que
            el alumno lo va a leer, se autocensura y el dato deja de servir. */}
        <div className="pp-privado">
          <span aria-hidden="true">🔒</span>
          <span>
            Esto es <b>interno</b>. El alumno nunca lo ve. Sirve para ajustarle
            la dificultad y recomendarle practica — se sincero, es lo util.
          </span>
        </div>

        {SENALES.map(({ clave, titulo, pie }) => (
          <div className="pp-campo" key={clave}>
            <label className="pp-label">{titulo}</label>
            <div className="pp-escala">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`pp-punto${valores[clave] === n ? ' pp-on' : ''}`}
                  onClick={() => ponValor[clave](n)}
                  aria-pressed={valores[clave] === n}
                  aria-label={`${titulo}: ${n} de 5`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="pp-escala-pies">
              <span>{pie[0]}</span>
              <span>{pie[1]}</span>
            </div>
          </div>
        ))}

        <div className="pp-campo">
          <label className="pp-label">Dificultad de sus ejercicios</label>
          <div className="pp-opciones">
            {DIFICULTADES.map(({ valor, etiqueta }) => (
              <button
                key={valor}
                type="button"
                className={`pp-opcion${dificultad === valor ? ' pp-on' : ''}`}
                onClick={() => setDificultad(valor)}
                aria-pressed={dificultad === valor}
              >
                {etiqueta}
              </button>
            ))}
          </div>
        </div>

        <div className="pp-campo">
          <label className="pp-label" htmlFor="pp-nota">
            Lo que los numeros no dicen (opcional)
          </label>
          <textarea
            id="pp-nota"
            className="pp-area"
            value={nota}
            maxLength={tope}
            onChange={(e) => escribeNota(e.target.value)}
            placeholder="Le cuesta el pasado simple cuando se pone nervioso…"
          />
          {/* El contador se enseña SIEMPRE, tambien con la caja vacia: un tope
              que solo se ve al cruzarlo no esta puesto para quien escribe. */}
          <span
            className={`pp-contador${lleno ? ' pp-tope' : cerca ? ' pp-cerca' : ''}`}
          >
            {nota.length} / {tope}
          </span>
        </div>

        {evaluar.isError && (
          <div className="pp-error">{evaluar.error?.message}</div>
        )}

        <div className="pp-acciones">
          <button className="pp-btn" onClick={onCerrar} disabled={evaluar.isPending}>
            Cancelar
          </button>
          <button
            className="pp-btn pp-btn-ac"
            onClick={guardar}
            disabled={evaluar.isPending}
          >
            {evaluar.isPending ? 'Guardando…' : 'Guardar evaluacion'}
          </button>
        </div>
      </div>
    </div>
  );
}
